import { parseApiError } from "./parseApiError";

/**
 * Global toast helper. Dispatches a window event that <Toast /> listens for.
 * Same window-event pattern as PermissionDeniedToast — works from anywhere
 * (components, hooks, fetch interceptors) without needing context plumbing.
 *
 * Usage:
 *   showToast.error("Couldn't delete order");
 *   showToast.success("Saved");
 *   showToast.fromApiError(response, body);   // auto-builds friendly message
 *
 * It can also be CALLED: showToast("Saved", { type: "success", duration: 2000 }).
 * Sales Intake (pages/admin/LiveCalls.js, components/calls/CallHistoryTable.js)
 * has called it that way since June 2026 — and while this was a plain object,
 * every one of those 35 calls threw "showToast is not a function". On a type
 * change the throw landed in the catch that puts the old value back, so a
 * change that HAD saved looked like it had not (found Oct 5 2026). Both forms
 * are supported so neither can break the other again.
 */

const dispatch = (detail) => {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent("chariot:toast", { detail }));
};

const idCounter = { n: 0 };
const newId = () => `t-${Date.now()}-${++idCounter.n}`;

const kinds = {
  // Success — green, 3s default
  success(message, opts = {}) {
    dispatch({
      id: newId(),
      kind: "success",
      title: opts.title || null,
      message,
      durationMs: opts.durationMs ?? 3000,
    });
  },

  // Error — red, 5s default (longer so users can read what went wrong)
  error(message, opts = {}) {
    dispatch({
      id: newId(),
      kind: "error",
      title: opts.title || null,
      message,
      durationMs: opts.durationMs ?? 5000,
    });
  },

  // Info — neutral grey/blue, 3s
  info(message, opts = {}) {
    dispatch({
      id: newId(),
      kind: "info",
      title: opts.title || null,
      message,
      durationMs: opts.durationMs ?? 3000,
    });
  },

  // Warning — amber, 4s
  warning(message, opts = {}) {
    dispatch({
      id: newId(),
      kind: "warning",
      title: opts.title || null,
      message,
      durationMs: opts.durationMs ?? 4000,
    });
  },

  /**
   * Build a toast directly from an API error using parseApiError. This is the
   * preferred way to surface mutation failures — guarantees consistent copy
   * across the whole app.
   *
   *   try { await fetch(...); }
   *   catch (err) { showToast.fromApiError(err); }
   *
   *   const res = await fetch(...);
   *   if (!res.ok) {
   *     const body = await res.json().catch(() => null);
   *     showToast.fromApiError(res, body);
   *   }
   */
  fromApiError(input, body = null, opts = {}) {
    const parsed = parseApiError(input, body);
    dispatch({
      id: newId(),
      kind: "error",
      title: opts.title || parsed.title,
      message: parsed.message,
      durationMs: opts.durationMs ?? 5000,
    });
  },
};

const KINDS = ["success", "error", "info", "warning"];

/** showToast(message, { type = "info", duration | durationMs, title }) */
function callToast(message, opts = {}) {
  const { type, duration, ...rest } = opts || {};
  const kind = KINDS.includes(type) ? type : "info";
  kinds[kind](message, duration != null && rest.durationMs == null ? { ...rest, durationMs: duration } : rest);
}

export const showToast = Object.assign(callToast, kinds);
